import { ChevronDown, Filter, ScanSearch, Search } from 'lucide-react'
import { motion } from 'motion/react'
import { Fragment, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { transitions } from '@/lib/motion/presets'
import { evaluationLabel, SITE_HEALTH_CATEGORIES } from '@/lib/siteHealth'
import type { SiteHealthEvaluationState } from '@/lib/siteHealth.types'
import { useTrackerHref } from '@/provider/useTrackerHref'
import EvaluationIcon from './EvaluationIcon'
import PageFindingDetails from './PageFindingDetails'
import { comparePageUrls, formatCount, pageLabel } from './SiteHealthFormat'
import { CARD_CLASS, EYEBROW_CLASS } from './SiteHealthStyles'
import type {
  SiteHealthPageTableProps,
  SiteHealthSeverityFilter,
  SiteHealthShortCategoryLabels,
} from './siteHealthComponents.types'

function isSeverityFilter(value: string): value is SiteHealthSeverityFilter {
  return (
    value === 'all' ||
    value === 'critical' ||
    value === 'warning' ||
    value === 'info'
  )
}

const SHORT_CATEGORY_LABELS: SiteHealthShortCategoryLabels = {
  access: 'Access',
  rendering: 'Render',
  structure: 'Struct',
  structured_data: 'Schema',
  content_extractability: 'Extract',
}

function pageMatchesSeverity(
  page: SiteHealthPageTableProps['pages'][number],
  severity: SiteHealthSeverityFilter
): boolean {
  if (severity === 'all') return true

  return page.findings.some((finding) => finding.severity === severity)
}

export default function SiteHealthPageTable({
  auditId,
  pages,
  nextCursor,
  onNextPage,
  onPreviousPage,
  hasPreviousPage = false,
  onFindingExpanded,
  onFiltersChange,
}: SiteHealthPageTableProps) {
  const href = useTrackerHref()
  const [filter, setFilter] = useState('')
  const [severity, setSeverity] = useState<SiteHealthSeverityFilter>('all')
  const [expandedPageId, setExpandedPageId] = useState<string | null>(null)
  const visiblePages = useMemo(() => {
    const normalizedFilter = filter.trim().toLocaleLowerCase()

    return pages
      .filter((page) => {
        const matchesFilter =
          normalizedFilter.length === 0 ||
          page.url.toLocaleLowerCase().includes(normalizedFilter)

        return matchesFilter && pageMatchesSeverity(page, severity)
      })
      .sort((left, right) => comparePageUrls(left.url, right.url))
  }, [filter, pages, severity])

  function togglePage(page: SiteHealthPageTableProps['pages'][number]) {
    const next = expandedPageId === page.id ? null : page.id

    setExpandedPageId(next)

    if (next) {
      const firstFinding = page.findings.find(
        (finding) => finding.evaluationState !== 'passed'
      )

      if (firstFinding) onFindingExpanded?.(firstFinding.id)
    }
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div className="flex flex-wrap items-center gap-3">
          <label className="relative block">
            <span className="sr-only">Filter pages</span>
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              type="search"
              aria-label="Filter pages"
              value={filter}
              onChange={(event) => {
                setFilter(event.currentTarget.value)
                onFiltersChange?.()
              }}
              placeholder="Filter pages"
              className="h-10 w-64 rounded-full bg-card pl-9"
            />
          </label>
          <div className="inline-flex items-center gap-2">
            <Filter
              className="size-4 text-muted-foreground"
              aria-hidden="true"
            />
            <Select
              aria-label="Filter severity"
              value={severity}
              onValueChange={(value) => {
                if (isSeverityFilter(value)) {
                  setSeverity(value)
                  onFiltersChange?.()
                }
              }}
            >
              <SelectTrigger className="h-10 rounded-full bg-card">
                <SelectValue placeholder="Filter severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All findings</SelectItem>
                <SelectItem value="critical">Critical only</SelectItem>
                <SelectItem value="warning">Warning only</SelectItem>
                <SelectItem value="info">Info only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-sm tabular-nums text-muted-foreground">
          Showing {visiblePages.length} of {pages.length} pages
        </p>
      </div>

      <div className={`${CARD_CLASS} overflow-x-auto`}>
        <table className="w-full min-w-[820px] text-left text-sm">
          <caption className="sr-only">Site Health findings by page</caption>
          <thead className={`bg-muted/50 ${EYEBROW_CLASS}`}>
            <tr>
              <th scope="col" className="px-5 py-3 font-medium">
                Page
              </th>
              {SITE_HEALTH_CATEGORIES.map((category) => (
                <th
                  key={category}
                  scope="col"
                  className="px-3 py-3 text-center font-medium"
                >
                  {SHORT_CATEGORY_LABELS[category]}
                </th>
              ))}
              <th scope="col" className="px-3 py-3 text-right font-medium">
                Text
              </th>
              <th scope="col" className="px-3 py-3 text-right font-medium">
                Raw → rendered
              </th>
              <th scope="col" className="w-12 px-3 py-3">
                <span className="sr-only">Expand</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {visiblePages.map((page) => {
              const expanded = expandedPageId === page.id
              const renderedTextCharacters = page.metrics.hasRenderedHtml
                ? page.metrics.renderedTextCharacters
                : null

              return (
                <Fragment key={page.id}>
                  <tr
                    onClick={() => togglePage(page)}
                    className={`cursor-pointer align-middle transition-colors hover:bg-muted/30 ${expanded ? 'bg-muted/20' : ''}`}
                  >
                    <th
                      scope="row"
                      className="max-w-[420px] px-5 py-3.5 font-medium text-foreground"
                    >
                      <Button
                        type="button"
                        variant="link"
                        aria-expanded={expanded}
                        aria-label={`${expanded ? 'Collapse' : 'Expand'} findings for ${pageLabel(page.url)}`}
                        onClick={(event) => {
                          event.stopPropagation()
                          togglePage(page)
                        }}
                        className="h-auto max-w-full justify-start whitespace-normal break-all p-0 text-left text-sm"
                      >
                        {pageLabel(page.url)}
                      </Button>
                    </th>
                    {SITE_HEALTH_CATEGORIES.map((category) => {
                      const state: SiteHealthEvaluationState =
                        page.categoryStates?.[category] ?? 'not_evaluated'
                      const label = evaluationLabel(state)

                      return (
                        <td key={category} className="px-3 py-3.5 text-center">
                          <span className="inline-flex items-center justify-center">
                            <EvaluationIcon state={state} />
                            <span className="sr-only">{label}</span>
                          </span>
                        </td>
                      )
                    })}
                    <td className="px-3 py-3.5 text-right tabular-nums text-muted-foreground">
                      {formatCount(page.metrics.visibleTextCharacters)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3.5 text-right tabular-nums text-muted-foreground">
                      {formatCount(page.metrics.rawTextCharacters)} →{' '}
                      {renderedTextCharacters !== null
                        ? formatCount(renderedTextCharacters)
                        : 'n/a'}
                    </td>
                    <td className="px-3 py-3.5 text-muted-foreground">
                      <ChevronDown
                        className={`size-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
                        aria-hidden="true"
                      />
                    </td>
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={9} className="bg-muted/20 px-5 py-5">
                        <motion.div
                          initial={{ opacity: 0, y: -6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={transitions.panel}
                        >
                          {page.hasSnapshot && (
                            <div className="mb-4">
                              <Button asChild variant="outline" size="sm">
                                <Link
                                  to={href(
                                    `/site-health/${auditId}/pages/${page.id}`
                                  )}
                                >
                                  <ScanSearch className="size-4" aria-hidden />
                                  Open page inspector
                                </Link>
                              </Button>
                            </div>
                          )}
                          <PageFindingDetails page={page} />
                        </motion.div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
        {visiblePages.length === 0 && (
          <p className="px-6 py-10 text-center text-sm text-muted-foreground">
            No pages match this filter.
          </p>
        )}
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          onClick={onPreviousPage}
          disabled={!hasPreviousPage}
          variant="outline"
        >
          Previous
        </Button>
        <Button
          type="button"
          onClick={onNextPage}
          disabled={!nextCursor}
          variant="outline"
        >
          Next page
        </Button>
      </div>
    </section>
  )
}
